import React from "react";
import { useIsDesktopViewport } from "../../../hooks/useIsDesktopViewport";
import { PAGE_LAYOUT } from "../../../layout/pageLayout";
import { DeskPanel } from "../../desktop/DeskTable";
import "../payrollDesktop.css";
import { CrewPilotModuleShell } from "../../CrewPilotModuleShell";
import {
  DEFAULT_PAYROLL_REPORT_OPTIONS,
  PAYROLL_REPORT_OPTION_DEFS,
} from "../../../helpers/payrollReport";

const CARD_STYLE = {
  background: "rgba(255,255,255,0.04)",
  border: "1px solid rgba(201,168,76,0.25)",
  borderRadius: "14px",
  padding: "20px",
  marginBottom: "16px",
};

const SECTION_TITLE_STYLE = {
  fontWeight: 700,
  fontSize: "15px",
  color: "#c9a84c",
  marginBottom: "10px",
};

const CHECK_ROW_STYLE = {
  display: "flex",
  alignItems: "center",
  gap: "12px",
  padding: "10px 0",
  borderBottom: "1px solid rgba(201,168,76,0.12)",
  fontSize: "14px",
};

const RADIO_GROUP_STYLE = {
  marginLeft: "30px",
  marginBottom: "8px",
};

const RADIO_ROW_STYLE = {
  display: "flex",
  alignItems: "center",
  gap: "10px",
  padding: "8px 0",
  fontSize: "14px",
  cursor: "pointer",
};

function CheckboxRow({ label, checked, disabled, onChange }) {
  return (
    <label
      style={{
        ...CHECK_ROW_STYLE,
        opacity: disabled ? 0.45 : 1,
        cursor: disabled ? "not-allowed" : "pointer",
      }}
    >
      <input
        type="checkbox"
        checked={checked}
        disabled={disabled}
        onChange={onChange}
        style={{ width: "18px", height: "18px", accentColor: "#c9a84c" }}
      />
      <span>{label}</span>
    </label>
  );
}

function RadioRow({ label, checked, name, onChange }) {
  return (
    <label style={RADIO_ROW_STYLE}>
      <input
        type="radio"
        name={name}
        checked={checked}
        onChange={onChange}
        style={{ accentColor: "#c9a84c" }}
      />
      <span>{label}</span>
    </label>
  );
}

export function PayrollReportOptionsScreen({
  reportTitle,
  options,
  onChange,
  onPreview,
  onBack,
  parentLabel,
}) {
  const isDesktop = useIsDesktopViewport();
  const mergedOptions = { ...DEFAULT_PAYROLL_REPORT_OPTIONS, ...options };
  const isDetailed = mergedOptions.detailLevel === "detailed";

  function update(patch) {
    onChange?.({ ...mergedOptions, ...patch });
  }

  function toggle(key) {
    update({ [key]: !mergedOptions[key] });
  }

  const headerOptions = PAYROLL_REPORT_OPTION_DEFS.filter(
    (def) =>
      def.key === "showEmployeeInformation" || def.key === "showPayrollPeriod",
  );
  const detailOptions = PAYROLL_REPORT_OPTION_DEFS.filter(
    (def) =>
      def.key !== "showEmployeeInformation" &&
      def.key !== "showPayrollPeriod" &&
      def.key !== "showTotals" &&
      def.key !== "showLabourDetails",
  );
  const footerOptions = PAYROLL_REPORT_OPTION_DEFS.filter(
    (def) => def.key === "showTotals" || def.key === "showNotes",
  );

  if (isDesktop) {
    const row = (def) => (
      <label key={def.key} className={`pay-opt-row${def.future ? " is-disabled" : ""}`}>
        <input type="checkbox" checked={!!mergedOptions[def.key]} disabled={def.future} onChange={() => !def.future && toggle(def.key)} />
        <span>{def.label}</span>
      </label>
    );
    return (
      <CrewPilotModuleShell
        title="Report Options"
        subtitle={reportTitle || "Payroll Report"}
        onBack={onBack}
        desktopBackLabel={parentLabel || "Payroll Queue"}
        layout={PAGE_LAYOUT.COMMAND}
        compact
        headerActions={
          <div className="cdh-actions">
            <button type="button" className="dt-btn dt-btn--primary" onClick={() => onPreview?.(mergedOptions)}>
              Preview Report
            </button>
          </div>
        }
      >
        <div className="pay-opt-grid">
          <div className="pay-opt-col">
            <DeskPanel title="Header">{headerOptions.map(row)}</DeskPanel>
            <DeskPanel title="Totals & Notes">{footerOptions.map(row)}</DeskPanel>
          </div>
          <DeskPanel title="Payroll Detail">
            <label className="pay-opt-row">
              <input type="checkbox" checked={!!mergedOptions.showLabourDetails} onChange={() => toggle("showLabourDetails")} />
              <span>Show Labour Details</span>
            </label>
            {mergedOptions.showLabourDetails ? (
              <>
                <div className="pay-seg" role="radiogroup" aria-label="Detail level" style={{ margin: "4px 0 10px 30px" }}>
                  <label>
                    <input type="radio" name="detailLevel" checked={mergedOptions.detailLevel === "summary"} onChange={() => update({ detailLevel: "summary" })} />
                    Summary Only
                  </label>
                  <label>
                    <input type="radio" name="detailLevel" checked={isDetailed} onChange={() => update({ detailLevel: "detailed" })} />
                    Detailed
                  </label>
                </div>
                {isDetailed ? <div className="pay-opt-sub">{detailOptions.map(row)}</div> : null}
              </>
            ) : null}
          </DeskPanel>
        </div>
      </CrewPilotModuleShell>
    );
  }

  return (
    <CrewPilotModuleShell
      title="Report Options"
      subtitle={reportTitle || "Payroll Report"}
      sectionLabel="Payroll Report"
      onBack={onBack}
    >
      <div style={CARD_STYLE}>
        <div style={SECTION_TITLE_STYLE}>Header</div>
        {headerOptions.map((def) => (
          <CheckboxRow
            key={def.key}
            label={def.label}
            checked={mergedOptions[def.key]}
            disabled={def.future}
            onChange={() => !def.future && toggle(def.key)}
          />
        ))}
      </div>

      <div style={CARD_STYLE}>
        <div style={SECTION_TITLE_STYLE}>Payroll Detail</div>
        <CheckboxRow
          label="Show Labour Details"
          checked={mergedOptions.showLabourDetails}
          onChange={() => toggle("showLabourDetails")}
        />

        {mergedOptions.showLabourDetails && (
          <>
            <div style={RADIO_GROUP_STYLE}>
              <RadioRow
                name="detailLevel"
                label="Summary Only"
                checked={mergedOptions.detailLevel === "summary"}
                onChange={() => update({ detailLevel: "summary" })}
              />
              <RadioRow
                name="detailLevel"
                label="Detailed"
                checked={isDetailed}
                onChange={() => update({ detailLevel: "detailed" })}
              />
            </div>

            {isDetailed &&
              detailOptions.map((def) => (
                <CheckboxRow
                  key={def.key}
                  label={def.label}
                  checked={mergedOptions[def.key]}
                  disabled={def.future}
                  onChange={() => !def.future && toggle(def.key)}
                />
              ))}
          </>
        )}
      </div>

      <div style={CARD_STYLE}>
        <div style={SECTION_TITLE_STYLE}>Totals & Notes</div>
        {footerOptions.map((def) => (
          <CheckboxRow
            key={def.key}
            label={def.label}
            checked={mergedOptions[def.key]}
            disabled={def.future}
            onChange={() => !def.future && toggle(def.key)}
          />
        ))}
      </div>

      <button
        type="button"
        className="nx-btn nx-btn-gold"
        style={{ width: "100%" }}
        onClick={() => onPreview?.(mergedOptions)}
      >
        Preview Report
      </button>
    </CrewPilotModuleShell>
  );
}
